import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { OutputFormulaValueKind } from 'src/app/models/output-formulas/output-formula-value';

import {
  FormulaResolvedRefs,
  formulaMetaBadge,
  resolveFormulaRefs,
} from '../shared/formula-labels';

export type OutputFormulasSubTab = 'range' | 'ev';

const RANGE_KINDS: readonly OutputFormulaValueKind[] = ['rangeStills', 'rangeVideos', 'rangeBoth'];
const EV_KINDS: readonly OutputFormulaValueKind[] = ['evStills', 'evVideos', 'evBoth'];

@Component({
  selector: 'app-output-formulas-panel',
  templateUrl: './output-formulas-panel.component.html',
  styleUrls: ['./output-formulas-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OutputFormulasPanelComponent {
  @Input() outputFormulas: OutputFormula[] = [];
  @Input() fileSpecifications: FileSpecification[] = [];
  @Input() filenameConventions: FilenameConvention[] = [];
  @Input() loaded = false;
  @Input() canWrite = false;
  @Input() canAudit = false;

  @Output() createRange = new EventEmitter<void>();
  @Output() createEv = new EventEmitter<void>();
  @Output() updateFormula = new EventEmitter<OutputFormula>();
  @Output() deleteFormula = new EventEmitter<OutputFormula>();
  @Output() viewFormulaHistory = new EventEmitter<OutputFormula>();

  activeSubTab: OutputFormulasSubTab = 'range';

  get rangeFormulas(): OutputFormula[] {
    return this.outputFormulas.filter((row) => RANGE_KINDS.includes(row.value.kind));
  }

  get evFormulas(): OutputFormula[] {
    return this.outputFormulas.filter((row) => EV_KINDS.includes(row.value.kind));
  }

  get visibleFormulas(): OutputFormula[] {
    return this.activeSubTab === 'range' ? this.rangeFormulas : this.evFormulas;
  }

  metaBadge(row: OutputFormula) {
    return formulaMetaBadge(row);
  }

  refs(row: OutputFormula): FormulaResolvedRefs {
    return resolveFormulaRefs(row, this.fileSpecifications, this.filenameConventions);
  }

  onSubTabChanged(subTab: OutputFormulasSubTab): void {
    this.activeSubTab = subTab;
  }

  onCreateRange(): void {
    this.createRange.emit();
  }

  onCreateEv(): void {
    this.createEv.emit();
  }

  onUpdateFormula(row: OutputFormula): void {
    this.updateFormula.emit(row);
  }

  onDeleteFormula(row: OutputFormula): void {
    this.deleteFormula.emit(row);
  }

  onViewFormulaHistory(row: OutputFormula): void {
    this.viewFormulaHistory.emit(row);
  }
}
